import { useEffect, useState } from "react";
import api from "@/utils/api";
import { Loader2 } from "lucide-react";

/**
 * Shows a live order breakdown (item, shipping, tax, total) for the Buy Now dialog.
 * Re-fetches when buyer state/city or quantity changes.
 */
export const PurchaseBreakdown = ({ listing, quantity = 1, variant = null, buyerState = "", buyerCity = "", offerMessageId = null, shippingTier = null }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!listing?.listing_id) return;
    let cancelled = false;
    const fetchQuote = async () => {
      setLoading(true);
      try {
        const res = await api.post("/payments/quote", {
          listing_id: listing.listing_id,
          variant_id: variant?.variant_id || null,
          quantity,
          buyer_state: buyerState,
          buyer_city: buyerCity,
          buyer_country: "United States",
          offer_message_id: offerMessageId || null,
          shipping_tier: shippingTier || null
        });
        if (!cancelled) setQuote(res.data);
      } catch (e) {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // Debounce
    const t = setTimeout(fetchQuote, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [listing?.listing_id, variant?.variant_id, quantity, buyerState, buyerCity, offerMessageId, shippingTier]);

  if (!quote && !loading) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid="purchase-breakdown">
      {loading && !quote ? (
        <div className="flex items-center justify-center py-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Calculating...
        </div>
      ) : quote ? (
        <div className="space-y-1.5">
          <Row label={`Item${quantity > 1 ? ` × ${quantity}` : ""}`} value={quote.item_total} />
          <Row
            label={`Shipping${quote.shipping_method ? ` (${quote.shipping_method})` : ""}`}
            value={quote.shipping_cost}
            zeroText={quote.shipping_cost === 0 ? "Free" : null}
          />
          <Row
            label={`Tax${quote.tax_rate ? ` (${(quote.tax_rate * 100).toFixed(2)}%)` : ""}`}
            value={quote.tax_amount}
            hint={!buyerState ? "Enter state above to calculate" : null}
          />
          {quote.first_order_discount > 0 && (
            <div className="flex items-center justify-between text-emerald-700" data-testid="first-order-discount-row">
              <span className="font-medium">🎉 First-order discount</span>
              <span className="font-semibold">−${quote.first_order_discount.toFixed(2)}</span>
            </div>
          )}
          {quote.store_credit_applied > 0 && (
            <div className="flex items-center justify-between text-emerald-700" data-testid="store-credit-row">
              <span className="font-medium">💰 Store credit applied</span>
              <span className="font-semibold">−${quote.store_credit_applied.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between">
            <span className="font-semibold">Total</span>
            <span className="text-lg font-bold text-emerald-600" data-testid="breakdown-total">
              ${quote.total.toFixed(2)}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const Row = ({ label, value, zeroText, hint }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-600">{label}</span>
    <span className="font-medium text-slate-900">
      {hint ? <span className="text-xs text-slate-400 italic">{hint}</span> : (zeroText && value === 0) ? zeroText : `$${(value || 0).toFixed(2)}`}
    </span>
  </div>
);

export default PurchaseBreakdown;
